import { useState, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Upload,
  Download,
  FileSpreadsheet,
  Building2,
  Server,
  Radio,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

export default function BulkImportPage() {
  const { api } = useAuth();
  const [importing, setImporting] = useState(false);
  const [activeTab, setActiveTab] = useState("companies");
  const [results, setResults] = useState(null);
  const fileInputRef = useRef(null);

  const downloadTemplate = async (type) => {
    try {
      const response = await api.get(`/import/template/${type}`, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${type}_template.csv`;
      a.click();
      toast.success(`${type} template downloaded`);
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith(".csv")) {
      toast.error("Please select a CSV file");
      return;
    }

    setImporting(true);
    setResults(null);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await api.post(`/import/${activeTab}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setResults(response.data);

      if (response.data.failed === 0) {
        toast.success(`Successfully imported ${response.data.success} records`);
      } else {
        toast.warning(
          `Imported ${response.data.success} records, ${response.data.failed} failed`
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
      setResults({
        success: 0,
        failed: 1,
        errors: [error.response?.data?.detail || "Unknown error"],
      });
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  const importTypes = [
    {
      id: "companies",
      name: "Companies",
      icon: Building2,
      description: "Import partner companies with contact details",
      fields: ["name*", "email*", "net", "domain", "contact", "start_date"],
    },
    {
      id: "ssps",
      name: "SSP Endpoints",
      icon: Server,
      description: "Import Supply-Side Platform endpoints",
      fields: [
        "name*",
        "company_id*",
        "connection_type",
        "region",
        "ad_formats",
        "traffic_types",
        "default_bidfloor",
        "margin",
      ],
    },
    {
      id: "dsps",
      name: "DSP Endpoints",
      icon: Radio,
      description: "Import Demand-Side Platform endpoints",
      fields: [
        "name*",
        "company_id*",
        "endpoint_url*",
        "openrtb_version",
        "region",
        "ad_formats",
        "traffic_types",
        "margin",
      ],
    },
  ];

  const currentType = importTypes.find((t) => t.id === activeTab);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="bulk-import-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Upload className="h-8 w-8 text-accent" />
          Bulk Import
        </h1>
        <p className="text-muted-foreground">
          Import multiple records from CSV files
        </p>
      </div>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept=".csv"
        className="hidden"
      />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 max-w-lg">
          {importTypes.map((type) => (
            <TabsTrigger key={type.id} value={type.id} className="gap-2">
              <type.icon className="h-4 w-4" />
              {type.name}
            </TabsTrigger>
          ))}
        </TabsList>

        {importTypes.map((type) => (
          <TabsContent key={type.id} value={type.id} className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Import Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <type.icon className="h-5 w-5 text-accent" />
                    Import {type.name}
                  </CardTitle>
                  <CardDescription>{type.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div
                    className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-accent/50 transition-colors"
                    onClick={triggerFileSelect}
                    data-testid="drop-zone"
                  >
                    {importing ? (
                      <div className="space-y-4">
                        <Loader2 className="h-12 w-12 mx-auto text-accent animate-spin" />
                        <p className="text-sm text-muted-foreground">
                          Importing records...
                        </p>
                      </div>
                    ) : (
                      <>
                        <FileSpreadsheet className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                        <p className="text-sm font-medium mb-1">
                          Click to select CSV file
                        </p>
                        <p className="text-xs text-muted-foreground">
                          or drag and drop
                        </p>
                      </>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => downloadTemplate(type.id)}
                      data-testid="download-template-btn"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Download Template
                    </Button>
                  </div>

                  <div className="text-xs text-muted-foreground">
                    <p className="font-medium mb-1">Required fields:</p>
                    <div className="flex flex-wrap gap-1">
                      {type.fields.map((field) => (
                        <Badge
                          key={field}
                          variant={field.includes("*") ? "default" : "secondary"}
                          className="text-xs"
                        >
                          {field.replace("*", "")}
                          {field.includes("*") && (
                            <span className="text-red-300 ml-0.5">*</span>
                          )}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Results Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Import Results</CardTitle>
                  <CardDescription>
                    View the results of your last import
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {results ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                          <div className="flex items-center gap-2 text-emerald-500 mb-1">
                            <CheckCircle className="h-4 w-4" />
                            <span className="text-sm font-medium">Success</span>
                          </div>
                          <p className="text-2xl font-bold font-mono">
                            {results.success}
                          </p>
                        </div>
                        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                          <div className="flex items-center gap-2 text-red-500 mb-1">
                            <XCircle className="h-4 w-4" />
                            <span className="text-sm font-medium">Failed</span>
                          </div>
                          <p className="text-2xl font-bold font-mono">
                            {results.failed}
                          </p>
                        </div>
                      </div>

                      {results.success + results.failed > 0 && (
                        <div className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span>Success Rate</span>
                            <span className="font-mono">
                              {Math.round(
                                (results.success /
                                  (results.success + results.failed)) *
                                  100
                              )}
                              %
                            </span>
                          </div>
                          <Progress
                            value={
                              (results.success /
                                (results.success + results.failed)) *
                              100
                            }
                            className="h-2"
                          />
                        </div>
                      )}

                      {results.errors && results.errors.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-sm font-medium text-red-500 flex items-center gap-1">
                            <AlertCircle className="h-4 w-4" />
                            Errors ({results.errors.length})
                          </p>
                          <div className="max-h-40 overflow-y-auto space-y-1">
                            {results.errors.map((error, index) => (
                              <p
                                key={index}
                                className="text-xs text-muted-foreground bg-muted p-2 rounded"
                              >
                                {error}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p className="text-sm">No import results yet</p>
                      <p className="text-xs">
                        Upload a CSV file to see import results
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Instructions */}
            <Card>
              <CardHeader>
                <CardTitle>Import Instructions</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-sm dark:prose-invert max-w-none">
                <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                  <li>
                    Download the CSV template using the button above
                  </li>
                  <li>
                    Fill in your data following the template structure
                  </li>
                  <li>
                    For multiple values (like ad_formats), separate with
                    semicolons (;)
                  </li>
                  <li>
                    Required fields are marked with an asterisk (*)
                  </li>
                  <li>
                    Save your file as CSV (UTF-8 encoding recommended)
                  </li>
                  <li>
                    Upload the file by clicking the drop zone or dragging the
                    file
                  </li>
                </ol>

                {type.id === "ssps" && (
                  <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                    <p className="text-sm text-amber-600 dark:text-amber-400">
                      <strong>Note:</strong> The company_id must match an
                      existing company. Create companies first before importing
                      SSPs.
                    </p>
                  </div>
                )}

                {type.id === "dsps" && (
                  <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                    <p className="text-sm text-amber-600 dark:text-amber-400">
                      <strong>Note:</strong> The company_id must match an
                      existing company, and endpoint_url is required for each
                      DSP.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
